import DefaultImageView from 'components/common/DefaultImageView';
import DefaultText from 'components/common/DefaultText';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {minmax} from 'utils/minmax';

/**
 * 등록 서명 컴포넌트의 Props 인터페이스
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setEnableButton - 버튼 활성화 상태 설정 함수
 * @property {React.Dispatch<React.SetStateAction<Blob | undefined>>} setSignature - 서명 설정 함수
 * @property {React.Dispatch<React.SetStateAction<Blob | undefined>>} setSignatureThumbnail - 서명 썸네일 설정 함수
 */
interface props {
  setEnableButton: React.Dispatch<React.SetStateAction<boolean>>;
  setSignature: React.Dispatch<React.SetStateAction<Blob | undefined>>;
  setSignatureThumbnail: React.Dispatch<React.SetStateAction<Blob | undefined>>;
}

/**
 * 좌표 인터페이스
 * @property {number} x - X 좌표
 * @property {number} y - Y 좌표
 */
interface Coordinate {
  x: number;
  y: number;
}

/**
 * 등록 서명 컴포넌트
 * 캔버스를 사용하여 서명을 입력하는 컴포넌트입니다.
 * @param {props} props - 등록 서명 컴포넌트의 props
 * @returns {JSX.Element} 등록 서명 컴포넌트
 */
export default function RegisterSignature({
  setEnableButton,
  setSignature,
  setSignatureThumbnail,
}: props) {
  // Canvas Ref
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Canvas 컨테이너 Ref
  const canvasContainerRef = useRef<HTMLDivElement | null>(null);

  // 상태관리
  const [mousePosition, setMousePosition] = useState<Coordinate | undefined>(
    undefined,
  );

  // Canvas 초기 세팅 (배경 흰색)
  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }
    const canvas: HTMLCanvasElement = canvasRef.current;
    const context = canvas.getContext('2d');

    if (context) {
      context.fillStyle = '#FFFFFF';
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, []);
  const [isPainting, setIsPainting] = useState(false);

  /**
   * 좌표 가져오기 함수
   * 마우스 이벤트에서 캔버스 좌표를 계산합니다.
   * @param {MouseEvent} event - 마우스 이벤트
   * @returns {Coordinate | undefined} 좌표 또는 undefined
   */
  const getCoordinates = (event: MouseEvent): Coordinate | undefined => {
    if (!canvasRef.current) {
      return;
    }

    const canvas: HTMLCanvasElement = canvasRef.current;
    return {
      x: event.pageX - canvas.offsetLeft,
      y: event.pageY - canvas.offsetTop,
    };
  };

  /**
   * 선 그리기 함수
   * 캔버스에 선을 그립니다.
   * @param {Coordinate} originalMousePosition - 원래 마우스 위치
   * @param {Coordinate} newMousePosition - 새로운 마우스 위치
   * @returns {void}
   */
  const drawLine = (
    originalMousePosition: Coordinate,
    newMousePosition: Coordinate,
  ) => {
    if (!canvasRef.current) {
      return;
    }
    const canvas: HTMLCanvasElement = canvasRef.current;
    const context = canvas.getContext('2d');

    if (context) {
      context.strokeStyle = 'black'; // 선 색깔
      context.lineJoin = 'round'; // 선끝 모양
      context.lineWidth = 5; // 선 굵기

      context.beginPath();
      context.moveTo(originalMousePosition.x, originalMousePosition.y);
      context.lineTo(newMousePosition.x, newMousePosition.y);
      context.closePath();

      context.stroke();
    }
  };

  const startPaint = useCallback((event: MouseEvent) => {
    const coordinates = getCoordinates(event);
    if (coordinates) {
      setEnableButton(true);
      setIsPainting(true);
      setMousePosition(coordinates);
    }
  }, []);

  const paint = useCallback(
    (event: MouseEvent) => {
      event.preventDefault(); // drag 방지
      event.stopPropagation(); // drag 방지

      if (isPainting) {
        const newMousePosition = getCoordinates(event);
        if (mousePosition && newMousePosition) {
          drawLine(mousePosition, newMousePosition);
          setMousePosition(newMousePosition);
        }
      }
    },
    [isPainting, mousePosition],
  );

  const exitPaint = useCallback(() => {
    setIsPainting(false);
  }, []);

  const exitPaintleave = useCallback(() => {
    setIsPainting(false);
    saveSign();
  }, []);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }
    const canvas: HTMLCanvasElement = canvasRef.current;

    canvas.addEventListener('mousedown', startPaint);
    canvas.addEventListener('mousemove', paint);
    canvas.addEventListener('mouseup', exitPaint);
    canvas.addEventListener('mouseleave', exitPaintleave);

    window.addEventListener('resize', handleResize);

    return () => {
      // Unmount 시 이벤트 리스터 제거
      canvas.removeEventListener('mousedown', startPaint);
      canvas.removeEventListener('mousemove', paint);
      canvas.removeEventListener('mouseup', exitPaint);
      canvas.removeEventListener('mouseleave', exitPaintleave);

      window.removeEventListener('resize', handleResize);
    };
  }, [startPaint, paint, exitPaint, exitPaintleave]);

  /**
   * 캔버스 지우기 함수
   * 캔버스의 모든 내용을 지웁니다.
   * @returns {void}
   */
  const clearCanvas = () => {
    if (!canvasRef.current) {
      return;
    }

    const canvas: HTMLCanvasElement = canvasRef.current;
    const context = canvas.getContext('2d');
    if (context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      setEnableButton(false);
    }
  };

  /**
   * 서명 저장 함수
   * 캔버스의 내용을 이미지로 추출하여 저장합니다.
   * @returns {void}
   */
  const saveSign = () => {
    if (canvasRef.current) {
      canvasRef.current.toBlob(
        blob => {
          if (blob !== null) {
            setSignature(blob);
          }
        },
        'image/png',
        1,
      );

      canvasRef.current.toBlob(
        blob => {
          if (blob !== null) {
            setSignatureThumbnail(blob);
          }
        },
        'image/png',
        0.5,
      );
    }
  };

  const [canvasSize, setCanvasSize] = useState({width: 0, height: 0});

  // Canvas 사이즈 조절
  useEffect(() => {
    if (canvasContainerRef.current) {
      setCanvasSize({
        width: canvasContainerRef.current.offsetWidth * 0.9,
        height: canvasContainerRef.current.offsetWidth * 0.6,
      });
    }
  }, [window.innerWidth]);

  const handleResize = () => {
    if (canvasContainerRef.current) {
      setCanvasSize({
        width: canvasContainerRef.current.offsetWidth * 0.9,
        height: canvasContainerRef.current.offsetWidth * 0.6,
      });
    }
  };

  return (
    <Container ref={canvasContainerRef}>
      <DefaultText
        text={'pleaseDrawSignature'}
        textStyle={{color: '#585858'}}
      />
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        style={{
          border: 'solid #cbcbcb 1px',
          borderRadius: '10px',
          marginTop: minmax(24),
          backgroundColor: 'white',
        }}
      />
      <div
        style={{
          display: 'flex',
          marginBottom: minmax(20),
          width: '90%',
          justifyContent: 'flex-end',
          alignItems: 'center',
          paddingTop: minmax(10, 8),
          cursor: 'pointer',
        }}
        onClick={clearCanvas}>
        <DefaultImageView
          source={'images/common/refresh_red.png'}
          style={{
            width: 'auto',
            height: minmax(20, 14),
            marginRight: minmax(6),
          }}
        />
        <DefaultText
          text={'signatureInitialization'}
          textStyle={{
            color: '#e61f17',
            fontSize: minmax(16, 14),
            lineHeight: minmax(16, 14),
          }}
        />
      </div>
    </Container>
  );
}

const Container = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;
